import { z } from 'zod';

/**
 * Normaliza un RUT removiendo puntos y guiones
 * @param rut RUT con formato (ej: 12.345.678-9)
 * @returns RUT normalizado (ej: 123456789)
 */
export function normalizeRut(rut: string): string {
  return rut.replace(/\./g, '').replace(/-/g, '').toUpperCase();
}

/**
 * Separa el cuerpo y el dígito verificador de un RUT
 * @param rutComplete RUT completo normalizado (ej: 123456789)
 * @returns Objeto con cuerpo y dv
 */
export function separateRut(rutComplete: string): { body: string; dv: string } {
  const normalized = normalizeRut(rutComplete);
  const body = normalized.slice(0, -1);
  const dv = normalized.slice(-1);
  return { body, dv };
}

/**
 * Calcula el dígito verificador de un RUT usando módulo 11
 * @param rutBody Cuerpo del RUT sin dígito verificador
 * @returns Dígito verificador calculado (0-9 o K)
 */
export function calculateVerificationDigit(rutBody: string): string {
  let sum = 0;
  let multiplier = 2;

  // Recorrer de derecha a izquierda
  for (let i = rutBody.length - 1; i >= 0; i--) {
    sum += parseInt(rutBody[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }

  const remainder = sum % 11;
  const calculatedDv = 11 - remainder;

  if (calculatedDv === 11) return '0';
  if (calculatedDv === 10) return 'K';
  return calculatedDv.toString();
}

/**
 * Valida el formato básico de un RUT (regex)
 * Formatos aceptados:
 * - 12345678-9
 * - 12.345.678-9
 * El guion es OBLIGATORIO
 * @param rut RUT a validar
 * @returns true si el formato es válido
 */
export function isRutFormatValid(rut: string): boolean {
  // Acepta formatos: 12345678-9, 12.345.678-9 (guion OBLIGATORIO)
  const rutRegex = /^(\d{1,2}\.?\d{3}\.?\d{3}-[\dkK]|\d{7,8}-[\dkK])$/;
  return rutRegex.test(rut);
}

/**
 * Valida el dígito verificador de un RUT usando módulo 11
 * @param rutComplete RUT completo con guion (ej: 12345678-9)
 * @returns true si el dígito verificador es válido
 */
export function isRutModulo11Valid(rutComplete: string): boolean {
  const { body, dv } = separateRut(rutComplete);
  
  // Validar que el cuerpo tenga al menos 1 dígito
  if (body.length === 0 || !/^\d+$/.test(body)) {
    return false;
  }

  const calculatedDv = calculateVerificationDigit(body);
  return dv === calculatedDv;
}

/**
 * Valida un RUT completamente (formato + módulo 11)
 * @param rut RUT a validar
 * @returns true si el RUT es válido
 */
export function isRutValid(rut: string): boolean {
  if (!rut || rut.trim() === '') return false;
  
  // Validar formato
  if (!isRutFormatValid(rut)) return false;
  
  // Validar módulo 11
  return isRutModulo11Valid(rut);
}

/**
 * Validador Zod para RUT
 * Valida formato regex y dígito verificador mediante módulo 11
 */
export const rutSchema = z
  .string()
  .trim()
  .min(1, 'El RUT es requerido')
  .refine(
    (value) => {
      const normalized = normalizeRut(value);
      return normalized.length >= 2; // Mínimo: 1 dígito cuerpo + 1 dv
    },
    { message: 'El RUT debe tener al menos 2 caracteres' },
  )
  .refine(
    (value) => isRutFormatValid(value),
    { message: 'El formato del RUT no es válido (use formato 12345678-9)' },
  )
  .refine(
    (value) => isRutModulo11Valid(value),
    { message: 'El dígito verificador del RUT no es válido' },
  );

/**
 * Validador Zod opcional para RUT (puede ser null o undefined)
 */
export const rutSchemaOptional = z
  .union([z.string().trim(), z.null(), z.undefined()])
  .transform((value) => {
    // Convertir undefined y empty string a null
    if (value === undefined || value === '' || value === null) return null;
    return value;
  })
  .refine(
    (value) => {
      if (value === null) return true;
      const normalized = normalizeRut(value);
      return normalized.length >= 2;
    },
    { message: 'El RUT debe tener al menos 2 caracteres' },
  )
  .refine(
    (value) => {
      if (value === null) return true;
      return isRutFormatValid(value);
    },
    { message: 'El formato del RUT no es válido (use formato 12345678-9)' },
  )
  .refine(
    (value) => {
      if (value === null) return true;
      return isRutModulo11Valid(value);
    },
    { message: 'El dígito verificador del RUT no es válido' },
  );
